import { AlertTriangle, CornerDownLeft, MessageCircleQuestion, Square } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Markdown } from "@/components/shared/Markdown";
import { Button } from "@/components/ui/button";
import { Badge, Textarea } from "@/components/ui/primitives";
import {
  EmptyState,
  EngineStatus,
  ErrorState,
  LoadingState,
  RestrictedState,
} from "@/components/feedback";
import { STREAM_EMPTY_ERROR, useAiStream } from "@/hooks/use-ai-stream";
import { useEngine } from "@/hooks/use-engine";
import { keys } from "@/hooks/queries";
import { queryClient } from "@/lib/api";
import { toUserError } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { useTabs, type TabDesc } from "@/stores/tabs";

interface Turn {
  role: "user" | "assistant";
  text: string;
  modelId?: string;
  costUsd?: number;
  /** Set when this turn's generation ended in a stream error — the `text`
   * alongside it may still hold whatever streamed in before the failure (a
   * transient error must never drop in-progress work). Raw (never rendered
   * directly — always passed through `toUserError`). */
  error?: string;
}

/** Banner-worthy engine states while a conversation is already under way —
 * `ready`/`processing`/`completed`/`waiting` are already obvious from the
 * transcript itself (the streaming caret, the disabled Ask button), so
 * showing an EngineStatus banner for those too would just be noise. */
const BANNER_STATES = new Set([
  "unavailable",
  "failed",
  "partial",
  "needs_auth",
  "needs_billing",
  "needs_permission",
  "paused",
]);

export function AskTab({ tab, visible }: { tab: TabDesc; visible: boolean }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState(tab.seedQuery ?? "");
  const conversationRef = useRef<string | undefined>(tab.conversationId);
  const { text: streamText, phase, start, stop } = useAiStream();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const seededRef = useRef(false);

  const send = async (query: string) => {
    const q = query.trim();
    if (!q || phase === "streaming") return;
    setInput("");
    setTurns((t) => [...t, { role: "user", text: q }]);
    const { text, meta } = await start("/api/ai/rag", {
      query: q,
      scope_document_ids: tab.scopeDocIds ?? [],
      scope_node_ids: tab.scopeNodeIds ?? [],
      conversation_id: conversationRef.current ?? null,
      k: 8,
    });
    if (meta.conversationId) {
      const fresh = conversationRef.current !== meta.conversationId;
      conversationRef.current = meta.conversationId;
      // Publish it so pins made anywhere attach to this active conversation,
      // and so a reload keeps this tab tied to the same conversation.
      useTabs.getState().setTabConversation(tab.id, meta.conversationId);
      // A new conversation needs to appear (with its title) in the Projects panel.
      if (fresh) void queryClient.invalidateQueries({ queryKey: keys.conversations });
    }
    // Always keep whatever text actually streamed in — even on failure — so a
    // transient error never erases a partial answer already on screen.
    setTurns((t) => [
      ...t,
      { role: "assistant", text, modelId: meta.modelId, costUsd: meta.costUsd, error: meta.error },
    ]);
    void queryClient.invalidateQueries({ queryKey: keys.usage });
  };

  // Fire the seed query exactly once when the tab is born with one — and
  // consume it from the (persisted) tab so a page reload never re-asks.
  useEffect(() => {
    if (tab.seedQuery && !seededRef.current) {
      seededRef.current = true;
      const seed = tab.seedQuery;
      useTabs.getState().consumeSeed(tab.id);
      setInput("");
      void send(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [turns.length, streamText]);

  const scoped = (tab.scopeDocIds?.length ?? 0) > 0 || (tab.scopeNodeIds?.length ?? 0) > 0;
  const lastUserQuery = [...turns].reverse().find((t) => t.role === "user")?.text ?? "";

  const engine = useEngine({
    stream: {
      phase,
      hasContent: streamText.trim().length > 0,
      error: phase === "error" ? (turns.at(-1)?.error ?? "The AI response failed.") : null,
    },
    onRetry: lastUserQuery ? () => void send(lastUserQuery) : undefined,
  });

  const introVisible = turns.length === 0 && phase !== "streaming";
  const showEngineBanner = !introVisible && BANNER_STATES.has(engine.state);

  return (
    <div className={cn("flex h-full min-h-0 flex-col", !visible && "hidden")}>
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <div className="mx-auto max-w-[72ch] space-y-5 pb-10">
          {introVisible &&
            (engine.state === "initializing" ? (
              <LoadingState label="Connecting…" className="pt-16" />
            ) : engine.state === "needs_auth" ? (
              <RestrictedState reason="auth" className="pt-16" />
            ) : engine.state === "unavailable" ? (
              <ErrorState
                error={engine.lastError ?? new Error("The assistant is unavailable.")}
                onRetry={engine.retry}
                label="assistant"
                className="pt-16"
              />
            ) : (
              <EmptyState
                icon={MessageCircleQuestion}
                title={scoped ? "Ask this corner of your atlas" : "Ask your atlas"}
                description="Answers are assembled from your own graph — retrieved nodes, their neighbors, their parents — and cited with §chips you can fly to."
                className="pt-16"
              />
            ))}
          {turns.map((turn, i) =>
            turn.role === "user" ? (
              <p
                key={i}
                className="ml-auto w-fit max-w-[85%] rounded-lg rounded-br-sm border border-primary/25 bg-primary/10 px-3.5 py-2 text-[13.5px] text-foreground"
              >
                {turn.text}
              </p>
            ) : (
              <div key={i}>
                {turn.text && <Markdown text={turn.text} />}
                {turn.error && (
                  <p className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-relaxed text-destructive">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                    {/* STREAM_EMPTY_ERROR is already curated, non-technical
                        copy authored client-side (not a raw backend payload),
                        so it renders verbatim; every other turn error keeps
                        going through toUserError as before. */}
                    {turn.error === STREAM_EMPTY_ERROR
                      ? turn.error
                      : toUserError(new Error(turn.error)).message}
                  </p>
                )}
                {!turn.error && turn.modelId && (
                  <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">
                    {turn.modelId.split("/").pop()}
                  </p>
                )}
              </div>
            ),
          )}
          {phase === "streaming" && (
            <div className="stream-caret">
              <Markdown text={streamText} />
            </div>
          )}
        </div>
      </div>

      {showEngineBanner && (
        <div className="shrink-0 px-4 pt-3">
          <div className="mx-auto max-w-[72ch]">
            <EngineStatus engine={engine} variant="banner" />
          </div>
        </div>
      )}

      <form
        className="shrink-0 border-t border-border px-4 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send(input);
        }}
      >
        <div className="mx-auto flex max-w-[72ch] flex-col gap-1.5">
          {scoped && (
            <Badge className="w-fit border-primary/30 text-primary">
              scoped · {(tab.scopeDocIds?.length ?? 0) > 0 ? "document" : "selection"}
            </Badge>
          )}
          <div className="flex items-end gap-2">
            <Textarea
              id="ask-composer"
              name="ask-composer"
              aria-label="Ask a question"
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send(input);
                }
              }}
              placeholder="Ask in your own words… (↵ to send, ⇧↵ for a new line)"
              className="bg-muted"
            />
            {phase === "streaming" ? (
              <Button type="button" variant="ghost" onClick={stop}>
                <Square className="h-3.5 w-3.5" /> Stop
              </Button>
            ) : (
              <Button type="submit" variant="default" disabled={!input.trim()}>
                <CornerDownLeft className="h-4 w-4" />
                Ask
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
